// utils/availabilityUiHelpers.js
//
// ONE answer to "what state is this calendar date in", plus the Arabic vocabulary
// and colours for saying it. Three surfaces in SecurePropertyDetails.js render it:
//   - the day-detail modal status line
//   - the month-grid cell background (FullCalendar dayCellDidMount)
//   - the calendar legend
//
// WHY THIS FILE EXISTS: those three each derived the state inline, and they
// disagreed. All three tested `stopSell` and `booked` but none tested `blocked`,
// so a manually held date reported «متاح للحجز» in the modal and rendered as a
// plain free cell in the grid — telling ops a committed date was sellable. The
// day object already carried a correct `blocked` flag that no consumer read.
// Adding the missing branch in three places would have left the next state
// (relocation's "guest in house", or `inventory.hold`) free to reappear as the
// same bug a fourth time. One resolver, one answer.
//
// Arabic-only, per the Arabic-first mandate — Properties is not on the translated
// surface (I18N_STRATEGY Phase 2, not shipped). If it ever joins, these constants
// become t() calls in each consumer; the import boundary is what keeps that small.
// Same pattern as config/arrivalLabels.js and utils/lockUiHelpers.js.

export const AVAILABILITY_STATE = {
  BOOKED: 'BOOKED',
  BLOCKED: 'BLOCKED',
  STOP_SELL: 'STOP_SELL',
  PARTIAL: 'PARTIAL',
  AVAILABLE: 'AVAILABLE',
  UNKNOWN: 'UNKNOWN'
};

/**
 * Resolve a day's inventory + restrictions to exactly one state.
 *
 * PRECEDENCE — booked → blocked → stopSell → available — is a decision, not an
 * accident of testing order, and the two collisions it settles are deliberate:
 *
 *   • booked AND blocked  → BOOKED. A sold date is the fact ops must not act against.
 *   • blocked AND stopSell → BLOCKED. They are different commitments: stop-sell means
 *     "we chose not to sell it", blocked means "the unit is held". Held is the stronger
 *     claim, so it wins. They must never share a label (see AVAILABILITY_VIEW).
 *
 * AGREES WITH THE BOOKING ENGINE BY CONSTRUCTION: the backend's source of truth is
 * `PropertyAvailability.isAvailable` = `available > 0 && !stopSell`. Only AVAILABLE and
 * PARTIAL are returned under that condition; every other state is one the engine also
 * refuses to sell. If that virtual changes, change this with it — a calendar that
 * disagrees with the engine is the failure this whole module exists to prevent.
 *
 * @param {object} day
 * @param {number} day.available  sellable units left (`inventory.available`)
 * @param {number} day.booked     units sold (`inventory.booked`)
 * @param {number} day.blocked    units held (`inventory.blocked`)
 * @param {number} day.total      units in the room (`inventory.total`)
 * @param {boolean} day.stopSell  `restrictions.stopSell`
 * @param {number} day.bookingCount  bookings the calendar knows of but inventory may
 *                                   not yet reflect. The grid has no booking list and
 *                                   passes nothing; the modal passes its own.
 * @param {boolean} day.hasData   false when NO availability row exists for the date.
 *                                Callers must pass this — see UNKNOWN below.
 * @returns {string} one of AVAILABILITY_STATE
 */
export const resolveAvailabilityState = ({
  available = 0,
  booked = 0,
  blocked = 0,
  total = 0,
  stopSell = false,
  bookingCount = 0,
  hasData = true
} = {}) => {
  // "No row for this date" is NOT "zero units left". Both arrive here as available: 0
  // with nothing booked, so without this guard an unfetched date would be reported as
  // held — trading the old bug (unknown dates read as sellable) for its mirror image.
  // Neither guess is honest; say we do not know.
  if (!hasData) return AVAILABILITY_STATE.UNKNOWN;

  // Coerce defensively: a frontend deployed ahead of the backend will not receive
  // `total` (API_DATA_FETCHING_PATTERNS — read new fields defensively). Missing
  // total collapses to 0, which fails the `total > 1` guard below and falls back to
  // the single-unit path — i.e. exactly today's behaviour, never a wrong PARTIAL.
  const avail = Number(available) || 0;
  const sold = Number(booked) || 0;
  const held = Number(blocked) || 0;
  const units = Number(total) || 0;
  const bookings = Number(bookingCount) || 0;

  const hasBooking = sold > 0 || bookings > 0;

  // Someone set the held counter — a recorded fact.
  const explicitBlock = held > 0;

  // Nothing sellable and nothing to account for it. Inferred, not recorded: this is
  // the shape a cancellation that never restored availability leaves behind, and it
  // is the clause that actually fires in production — the `blocked` counter is
  // written by only one UI path, but zero-inventory-with-no-booking has several.
  const inferredBlock = avail <= 0 && !hasBooking && !explicitBlock;

  // Multi-unit only. With units still sellable and sales open, some units being spoken
  // for means FEWER, not none — reporting a flat BOOKED/BLOCKED here would repeat the
  // original bug in the opposite direction. Unreachable while total <= 1, because the
  // schema invariant (available + booked + blocked + hold <= total) makes
  // "available > 0 and something held" impossible on a single unit.
  if (units > 1 && avail > 0 && !stopSell && (hasBooking || explicitBlock)) {
    return AVAILABILITY_STATE.PARTIAL;
  }

  if (hasBooking) return AVAILABILITY_STATE.BOOKED;
  if (explicitBlock) return AVAILABILITY_STATE.BLOCKED;

  // A RECORDED reason outranks an INFERRED one. When stop-sell is set and the only
  // other signal is zero inventory, "we closed sales" is what we actually know;
  // reporting a hold instead would invent a cause the data never stated.
  if (stopSell) return AVAILABILITY_STATE.STOP_SELL;
  if (inferredBlock) return AVAILABILITY_STATE.BLOCKED;

  return AVAILABILITY_STATE.AVAILABLE;
};

/**
 * Arabic label + colours per state.
 *
 * ⚠️ THE NAMING TRAP THIS MAP DEFUSES: three different things in this system were all
 * called "blocked" — `restrictions.stopSell` (labelled «محظور» in the old UI), the
 * `inventory.blocked` counter, and the backend's `getDateStatus()`, which returns
 * 'blocked' for stop-sell and 'unavailable' for an actual block. Ops cannot act on a
 * word that means three things, so:
 *   • «محجوب (حجز داخلي)» — the unit is HELD
 *   • «إيقاف البيع»       — sales are CLOSED
 * «محظور» is deliberately retired from this surface. It reads as a near-synonym of
 * «محجوب» at a glance, and ar.js already spends it on denied browser permissions.
 *
 * `cellBg` is null where the month grid should stay unpainted: bookings already render
 * as coloured event bars over the cell, and painting available days would make a busy
 * calendar unreadable. Only the states with nothing else to show them get a wash.
 */
export const AVAILABILITY_VIEW = {
  [AVAILABILITY_STATE.BOOKED]: {
    label: '📅 محجوز',
    bg: '#f8d7da',
    border: '#f5c6cb',
    text: '#721c24',
    cellBg: null
  },
  [AVAILABILITY_STATE.BLOCKED]: {
    // NOT «حجز داخلي» — a block is not a booking, and borrowing «حجز» for it would
    // rebuild the very confusion this map exists to remove.
    label: '🔒 محجوب داخلياً',
    // ORANGE, not the obvious pale amber (#fff3cd). PropertyDetailsStyled.css tints
    // today's cell rgba(255,198,0,.12) -> ~#fff8e0, and .18 on mobile -> ~#fff5d1;
    // pale amber sat within a few points of that, so "held" and "today" were the same
    // colour on a phone. Keep a wide gap from the today tint when changing this.
    bg: '#ffe0b2',
    border: '#ffcc80',
    text: '#856404',
    cellBg: '#ffe0b2'
  },
  [AVAILABILITY_STATE.STOP_SELL]: {
    label: '🚫 إيقاف البيع',
    bg: '#e9ecef',
    border: '#dee2e6',
    text: '#495057',
    cellBg: '#e9ecef'
  },
  [AVAILABILITY_STATE.PARTIAL]: {
    label: '🟡 متاح جزئياً',
    bg: '#cfe2ff',
    border: '#b6d4fe',
    text: '#084298',
    cellBg: '#cfe2ff'
  },
  [AVAILABILITY_STATE.AVAILABLE]: {
    label: '✅ متاح للحجز',
    bg: '#d4edda',
    border: '#c3e6cb',
    text: '#155724',
    cellBg: null
  },
  // Neutral on purpose. Green would repeat the original lie in a new place, and amber
  // would assert a hold we have no evidence for.
  [AVAILABILITY_STATE.UNKNOWN]: {
    label: 'ℹ️ لا توجد بيانات توفر لهذا اليوم',
    bg: '#f8f9fa',
    border: '#dee2e6',
    text: '#495057',
    cellBg: null
  }
};

/**
 * A night nobody can book — held, or sales closed. Both calendars draw a thin diagonal line
 * ("/") over it, the convention hosts know from Airbnb: the line says "closed", the colour still
 * says WHY. One definition so the unit calendar and «التقويم» draw the same line; it works over
 * the orange and the grey wash alike.
 */
export const isClosedState = (state) =>
  state === AVAILABILITY_STATE.BLOCKED || state === AVAILABILITY_STATE.STOP_SELL;

export const CLOSED_NIGHT_LINE =
  'linear-gradient(to top right, transparent calc(50% - 0.75px), rgba(9, 0, 64, 0.3) calc(50% - 0.75px), '
  + 'rgba(9, 0, 64, 0.3) calc(50% + 0.75px), transparent calc(50% + 0.75px))';

/**
 * Never let an unrecognised state render blank — an empty status line reads as "fine".
 * Falls back to UNKNOWN, never to AVAILABLE: defaulting to green would reinstate the
 * original defect (say "sellable" when we do not actually know) one layer down.
 */
export const availabilityView = (state) =>
  AVAILABILITY_VIEW[state] || AVAILABILITY_VIEW[AVAILABILITY_STATE.UNKNOWN];
